<template>
    <!-- Mortality bar cell. Rail + fill-to-value + a target tick, sharing one ceiling across
         every Barn and Section row so bar length is directly comparable row-to-row (see
         objectiveBar.ts). Only ever rendered by the caller once currentValue and resolvedTarget are
         both known - a row with no target falls back to plain text instead (see BarnRow.vue/
         SectionRow.vue), so there is nothing to guard against null here.

         Thick rail, not squeezed into a table cell; the target NUMBER lives only here - on hover of
         the tick - rather than in its own column (see objectiveRow.ts's resolveFarmUniformTarget for
         the one case it's shown as plain text instead, in the Farm card header). The tick's hit box
         is padded wider than its 2px border since a bare border is otherwise near-impossible to point
         a cursor at. -->
    <div class="relative h-[10px] shrink-0 rounded-full bg-gray-200">
        <div class="absolute inset-y-0 left-0 rounded-full" :class="fillClass" :style="{ width: fillPercent + '%' }" />
        <div
            class="absolute inset-y-0 flex w-2 -translate-x-1/2 items-center justify-center"
            :style="{ left: tickPercent + '%' }"
            :title="$t('views.objectives.texts.targetTooltip', { value: $n(resolvedTarget, 'decimal') })"
        >
            <div class="h-[18px] w-0 border-l-2" :class="tickClass" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { TargetSource } from '@/types/MortalityObjective';
import { barFillPercent, barTickPercent } from './objectiveBar';
import { objectiveBarFillClass } from './objectiveDisplay';

const props = defineProps<{
    currentValue: number;
    resolvedTarget: number;
    // Only 'Override' vs anything-else matters here: dashed tick for an inherited Directory default,
    // solid for a Farm's own override, as a line style rather than a colour so it doesn't compete
    // with the fill's red/green verdict.
    targetSource: TargetSource;
    ceiling: number;
}>();

const fillPercent = computed(() => barFillPercent(props.currentValue, props.ceiling));
const tickPercent = computed(() => barTickPercent(props.resolvedTarget, props.ceiling));
const fillClass = computed(() => objectiveBarFillClass({ currentValue: props.currentValue, resolvedTarget: props.resolvedTarget }));
const tickClass = computed(() => (props.targetSource === 'Override' ? 'border-solid border-gray-700' : 'border-dashed border-gray-500'));
</script>
